import React from 'react';
import { formatObservationRef } from '../../../utils/intelligenceMeta';
import styles from '../css/index.module.css';

const SEVERITY_CLASS = {
    critical: styles.aiWardenSeverityCritical,
    high: styles.aiWardenSeverityHigh,
    medium: styles.aiWardenSeverityMedium,
    low: styles.aiWardenSeverityLow,
};

function RefLinks({ label, refs, onCitationClick }) {
    if (!refs?.length) return null;
    return (
        <div className={styles.aiWardenObservationRefs}>
            <span className={styles.aiWardenObservationRefsLabel}>{label}</span>
            {refs.map((ref, i) => {
                const formatted = formatObservationRef(ref);
                if (!formatted) return null;
                return (
                    <button
                        key={`${label}-${formatted}-${i}`}
                        type="button"
                        className={styles.aiWardenObservationRefBtn}
                        onClick={() => onCitationClick?.(formatted)}>
                        {formatted}
                    </button>
                );
            })}
        </div>
    );
}

const ObservationCard = ({ observation, onCitationClick }) => {
    if (!observation) return null;

    const severity = observation.ranked_severity || observation.severity;
    const severityClass = severity ? SEVERITY_CLASS[String(severity).toLowerCase()] : null;

    return (
        <article className={styles.aiWardenObservationCard}>
            <header className={styles.aiWardenObservationHeader}>
                <span className={styles.aiWardenObservationKicker}>Observation candidate</span>
                {severity && (
                    <span className={`${styles.aiWardenSeverityBadge}${severityClass ? ` ${severityClass}` : ''}`}>{severity}</span>
                )}
                {observation.requires_manual_review && (
                    <span className={styles.aiWardenReviewChip}>Needs review</span>
                )}
            </header>

            {observation.title && <h4 className={styles.aiWardenObservationTitle}>{observation.title}</h4>}
            {observation.narrative && <p className={styles.aiWardenObservationNarrative}>{observation.narrative}</p>}

            <dl className={styles.aiWardenObservationMeta}>
                {observation.finding_type && (
                    <>
                        <dt>Finding</dt>
                        <dd>{observation.finding_type}</dd>
                    </>
                )}
                {observation.dd_topic && (
                    <>
                        <dt>DD topic</dt>
                        <dd>{observation.dd_topic}</dd>
                    </>
                )}
                {observation.grounded_status && (
                    <>
                        <dt>Grounded</dt>
                        <dd>{observation.grounded_status}</dd>
                    </>
                )}
                {observation.business_impact && (
                    <>
                        <dt>Business impact</dt>
                        <dd>{observation.business_impact}</dd>
                    </>
                )}
                {observation.escalation_score != null && (
                    <>
                        <dt>Escalation</dt>
                        <dd>{observation.escalation_score}</dd>
                    </>
                )}
                {observation.consultant_priority != null && (
                    <>
                        <dt>Priority</dt>
                        <dd>{observation.consultant_priority}</dd>
                    </>
                )}
            </dl>

            {observation.recommendation && (
                <div className={styles.aiWardenObservationBlock}>
                    <span className={styles.aiWardenObservationBlockLabel}>Recommendation</span>
                    <p>{observation.recommendation}</p>
                </div>
            )}

            {observation.follow_up_questions?.length > 0 && (
                <div className={styles.aiWardenObservationBlock}>
                    <span className={styles.aiWardenObservationBlockLabel}>Follow-up questions</span>
                    <ul>
                        {observation.follow_up_questions.map((q, i) => (
                            <li key={i}>{q}</li>
                        ))}
                    </ul>
                </div>
            )}

            <RefLinks label="Supporting" refs={observation.supporting_refs} onCitationClick={onCitationClick} />
            <RefLinks label="Contradicting" refs={observation.contradicting_refs} onCitationClick={onCitationClick} />
            <RefLinks label="Evidence gaps" refs={observation.evidence_gap_refs} onCitationClick={onCitationClick} />

            <div className={styles.aiWardenObservationActions}>
                <button type="button" className={styles.aiWardenObservationActionDisabled} disabled title="Review coming soon">
                    Review coming soon
                </button>
            </div>
        </article>
    );
};

export default ObservationCard;
